import React, { ChangeEvent, useMemo, useState } from "react";
import IOrder from "../../../../models/IOrder";
import IOrder_Item from "../../../../models/IOrder_Item";
import IPackingListViewOptions from "../../../../models/IPackingListViewOptions";
import OwTranslate from "../../OwTranslate/OwTranslate";
import OwS3ImageViewer from "../../OwS3ImageViewer/OwS3ImageViewer";
import { OwBarcode } from "../../OwBarcode/OwBarcode";
import { CatalogItemDescription } from "../../CatalogItemDescription/CatalogItemDescription";
import OwCheckbox from "../../OwCheckbox/OwCheckbox";
import { isNil } from "lodash-es";
import OwInputWrapper from "../../OwInputWrapper/OwInputWrapper";
import { formatDimensionDisplay } from "@utils/DimensionConversion";
import ISupplierModel from "@models/ISupplierModel";
import { getFormattedDimensions } from "@services/OrderItemService";
import { FormattedNumber } from "react-intl";

type Props = {
    order: IOrder;
    orderItem: IOrder_Item;
    viewOptions: IPackingListViewOptions;
    supplierModel: ISupplierModel;
};

export const SimplePackingListItemRow = (props: Props) => {
    const [isDropshipped, setIsDropshipped] = useState(false);

    const toggleDropshipped = () => {
        setIsDropshipped(!isDropshipped);
    };

    const catalogItem = useMemo(() => {
        return props.orderItem.Catalog_Item!;
    }, [props.orderItem]);

    const getQty = () => {
        let _qty = props.orderItem.total_qty || 0;

        if (!props.orderItem.total_qty) {
            _qty = Number(props.orderItem.qty);
        }

        if (props.orderItem.bonus_qty_frozen) {
            _qty = Number(_qty) + Number(props.orderItem.bonus_qty_frozen);
        } else if (props.orderItem.total_bonus_qty_frozen) {
            _qty = Number(_qty) + Number(props.orderItem.total_bonus_qty_frozen);
        }

        return _qty;
    };

    const orderQty = useMemo(() => {
        return props.orderItem.qty || 0;
    }, [props.orderItem]);

    const [qty, setQty] = useState(getQty());

    const handleQtyChange = (e: ChangeEvent<any>) => {
        let value = e.target.value;
        const name = e.target.name;

        if (Number(value) > props.orderItem.qty) {
            value = props.orderItem.qty;
        }

        if (Number(value) < 0) {
            value = 0;
        }

        setQty(Number(value));
    };

    const note = useMemo(() => {
        if (!isNil(props.orderItem.note) && props.orderItem.note) {
            return (
                <div>
                    <OwTranslate defaultMessage={"note"} id={"note"} />: {props.orderItem.note}
                </div>
            );
        }
    }, [props.orderItem]);

    const barcodeOpts = {
        height: 75,
    };

    const { widthDetail, heightDetail, depthDetail } = getFormattedDimensions(props.orderItem);

    return (
        <tr>
            <td>{props.orderItem.item_no}</td>
            {props.viewOptions.showItemLocation ? (
                <td>
                    <div>{props.orderItem.Catalog_Item?.row}</div>
                    <div>{props.orderItem.Catalog_Item?.bin}</div>
                </td>
            ) : null}
            <td>
                <div>{catalogItem.name}</div>
                {props.viewOptions.showItemPhoto ? <OwS3ImageViewer catalogItem={catalogItem} /> : null}
                {props.viewOptions.showUpc && catalogItem.upc && catalogItem.upc.length === 12 ? (
                    <div>
                        <OwBarcode format={"UPC"} value={catalogItem.upc} height={50} />
                    </div>
                ) : null}
                {props.orderItem.Order_Item_Options && props.orderItem.Order_Item_Options.length > 0 ? <div className="mt-2"></div> : null}
                {props.orderItem.Order_Item_Options?.map((option) => {
                    return (
                        <div key={option.id}>
                            <b>{option.Item_Option?.name}:</b> {option.value}
                        </div>
                    );
                })}
            </td>
            {props.viewOptions.showSku ? (
                <td>
                    <div>{catalogItem.sku}</div>
                    {props.viewOptions.hideSkuCol && catalogItem.sku?.length ? (
                        <div>
                            <OwBarcode format={"CODE128"} value={catalogItem.sku} height={50} />
                        </div>
                    ) : null}
                </td>
            ) : null}
            {props.viewOptions.showUom ? <td>{catalogItem.Unit_Of_Measurement?.abbreviation}</td> : null}
            {props.viewOptions.showDimensions ? <td>{widthDetail}</td> : null}
            {props.viewOptions.showDimensions ? <td>{heightDetail}</td> : null}
            {props.viewOptions.showDimensions ? <td>{depthDetail}</td> : null}
            <td>
                <div className={"pre-wrap text-break"}>
                    <CatalogItemDescription catalogItem={catalogItem} orderItem={props.orderItem} priceGroup={props.order?.Price_Group} />
                </div>
                <div>{note}</div>
            </td>
            {props.viewOptions.showLot ? <td className={"w-10"}></td> : null}
            <td className={"w-15 " + (props.viewOptions.showOrderQty ? "" : "d-none")}>{orderQty}</td>
            <td className={"w-15 " + (props.viewOptions.showShipQty ? "" : "d-none")}>
                <span className={isDropshipped ? "d-none" : ""}>
                    <span className={"d-print-none"}>
                        <OwInputWrapper name={"ship_qty"} type="number" fn={handleQtyChange} value={qty} />
                    </span>
                    <span className={"d-none d-print-block"}>{qty}</span>
                </span>
                <span className={isDropshipped ? "" : "d-none"}>
                    <OwTranslate defaultMessage={"dropshipped"} id={"dropshipped"} />
                </span>
                <span className={(props.viewOptions.showDropshipment ? "" : "d-none") + " d-print-none"}>
                    <OwCheckbox label={"is_dropshipped"} name={"is_dropshipped"} fn={toggleDropshipped} />
                </span>
            </td>
            {props.viewOptions.showUnitPrice ? (
                <td className={"w-15 text-end" + (props.viewOptions.showUnitPrice ? "" : "d-none")}>
                    <FormattedNumber
                        style="currency"
                        currency="USD"
                        value={props.orderItem.net_price || 0}
                        maximumFractionDigits={props.supplierModel.pricing_precision || 2}
                    />
                </td>
            ) : null}
        </tr>
    );
};
